import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderClock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { UserMenu } from '../UserMenu';
import { CreditUsageInfo } from '../CreditUsageInfo';

// V37.44.40 — Friendly ladybug-style icon for the "Bug Fix" nav button.
// Replaces lucide `Bug` (whose 6 straight legs read as spider-like on
// small viewports). This inline SVG has a rounded dome body, two gentle
// antennae, a center seam, and four soft spots — a warmer, approachable
// insect without sharp or intimidating geometry.
// V37.44.41 — Enlarged body + antennae reach to fill the 24×24 viewBox
// so the rendered size matches the adjacent `FolderClock` icon (lucide
// icons occupy the full viewBox; the earlier ladybug sat ~75% of it).
const FriendlyBugIcon = ({ className = '', strokeWidth = 1.75, ...rest }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    {...rest}
  >
    {/* Rounded dome body — fills most of the 24×24 viewBox to match
        the visual weight of adjacent lucide icons. */}
    <ellipse cx="12" cy="13.5" rx="9" ry="7.5" />
    {/* Center seam (wing split) */}
    <path d="M12 6 L12 21" />
    {/* Four friendly spots */}
    <circle cx="8.25" cy="11.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="15.75" cy="11.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="8.25" cy="15.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="15.75" cy="15.5" r="1" fill="currentColor" stroke="none" />
    {/* Gentle antennae reaching to the viewBox edge */}
    <path d="M9 6.5 L7 2.5" />
    <path d="M15 6.5 L17 2.5" />
    <circle cx="7" cy="2.5" r="0.75" fill="currentColor" stroke="none" />
    <circle cx="17" cy="2.5" r="0.75" fill="currentColor" stroke="none" />
  </svg>
);

export const Header = ({ credits, showAuth = true, showCredits = false, minimal = false }) => {
  const navigate = useNavigate();
  const location = window.location;
  const { user, login, isAuthenticated, loading: authLoading } = useAuth();

  // V37.44.8 — Free-access mode. Credit UI is hidden site-wide; the prop
  // remains for backwards compatibility but defaults to false so no
  // caller needs to change. Internal accounting is unaffected.
  const _showCredits = false && showCredits && !minimal;
  const _showUserMenu = showAuth && isAuthenticated && !minimal;
  const _showSignIn   = showAuth && !isAuthenticated && !minimal;

  // V37.44.10 — Daily Cap Header Badge. Reads /api/generation-limit-status
  // (V37.44.8 endpoint) and shows "X / 10 free generations left today".
  // V37.44.12 — Restricted to homepage (/) only. Restyled with subtle red
  // border, black text, matching homepage background. Superuser accounts
  // are exempt (endpoint returns exempt:true) and the badge stays hidden.
  const [dailyRemaining, setDailyRemaining] = useState(null);
  const [dailyLimit, setDailyLimit] = useState(10);
  const [dailyExempt, setDailyExempt] = useState(false);
  const fetchDailyStatus = useCallback(async () => {
    if (!isAuthenticated || minimal) return;
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const r = await fetch(`${backend}/api/generation-limit-status`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      if (d && d.exempt === true) {
        setDailyExempt(true);
        setDailyRemaining(null);
        return;
      }
      setDailyExempt(false);
      if (d && typeof d.remaining === 'number') setDailyRemaining(d.remaining);
      if (d && typeof d.limit === 'number') setDailyLimit(d.limit);
    } catch (_e) { /* silent */ }
  }, [isAuthenticated, minimal]);
  useEffect(() => {
    fetchDailyStatus();
    const onFocus = () => fetchDailyStatus();
    const onGenDone = () => fetchDailyStatus();
    window.addEventListener('focus', onFocus);
    window.addEventListener('ep:generation-completed', onGenDone);
    const interval = setInterval(fetchDailyStatus, 60000);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('ep:generation-completed', onGenDone);
      clearInterval(interval);
    };
  }, [fetchDailyStatus]);
  const _isHome = location.pathname === '/';
  const _showDailyBadge = isAuthenticated && !minimal && _isHome && !dailyExempt && dailyRemaining !== null;

  const getCreditsDisplay = () => {
    if (authLoading) return '\u2014';
    if (user && user.credits !== undefined) return user.credits.toFixed(2);
    if (!isAuthenticated && credits !== null && credits !== undefined) return credits.toFixed(2);
    return '0.00';
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, transform: 'translateZ(0)', backfaceVisibility: 'hidden', willChange: 'auto' }}
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto flex items-center gap-3" style={{ height: '56px', padding: '8px 16px' }}>
        <div className="flex items-center space-x-1 sm:space-x-1.5 md:space-x-2.5 flex-shrink-0 mr-auto">
          <div className="flex items-center space-x-1 sm:space-x-1.5 md:space-x-2.5 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-10 md:h-10 bg-black rounded-xl flex items-center justify-center p-1 sm:p-1.5 flex-shrink-0">
              <img 
                src="/ep-crow-logo-sm.png" 
                alt="EP" 
                className="w-full h-full object-contain" 
                loading="eager" 
                decoding="sync" 
                fetchPriority="high"
                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span style="color:white;font-weight:bold;font-size:14px">EP</span>'; }}
              />
            </div>
            <div className="min-w-0 overflow-hidden">
              <h1 className="text-xs sm:text-sm md:text-lg font-bold text-black leading-tight truncate">ElonPrompt AI</h1>
              <p className="text-[9px] sm:text-[10px] md:text-[10px] text-gray-500 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">VIBE TRANSFORMER</p>
            </div>
          </div>
        </div>

        {/* V37.44.43 — Flat right-cluster: Folder → Bug → Pill → UserMenu
            with uniform gap-3. Bug icon is a standalone button again
            (outside the daily pill). Pill restored to clean text-only
            "N / N left" with native whitespace. Routes unchanged. */}
        {location.pathname !== '/' && (
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-1 md:space-x-1.5 px-2 md:px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-xs md:text-sm flex-shrink-0"
            data-testid="back-to-home-btn"
          >
            <span className="text-sm md:text-base">&#127968;</span>
            <span className="font-medium hidden sm:inline">Back to Home</span>
            <span className="font-medium sm:hidden">Home</span>
          </button>
        )}

        {/* V37.44.46 — On the Home route the Project/Bug Fix History links
            are rendered inline above the privacy notice in Home.js (b1
            position per UX refinement). They remain in the header on
            every other route as "Project Prompts" / "Bug Fix Prompts". */}
        {_showUserMenu && !_isHome && (() => {
          const _projectLabel = _isHome ? 'Project History' : 'Project Prompts';
          const _bugLabel = _isHome ? 'Bug Fix History' : 'Bug Fix Prompts';
          return (
            <>
              {/* Project Prompts / History */}
              <button
                type="button"
                onClick={() => navigate('/prompts/projects')}
                aria-label={_projectLabel}
                title={_projectLabel}
                className="flex items-center justify-center p-1.5 sm:p-0 rounded-md sm:rounded-sm text-gray-900 hover:text-black active:text-black hover:bg-gray-100 sm:hover:bg-transparent sm:hover:underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 transition-colors whitespace-nowrap min-h-[36px] min-w-[36px] sm:min-h-0 sm:min-w-0 flex-shrink-0"
                data-testid="header-project-prompts-link"
              >
                <FolderClock className="w-5 h-5 sm:hidden" strokeWidth={1.75} aria-hidden="true" />
                <span className="hidden sm:inline text-sm md:text-base font-medium">{_projectLabel}</span>
              </button>

              {/* Bug Fix Prompts / History */}
              <button
                type="button"
                onClick={() => navigate('/prompts/bug-fixes')}
                aria-label={_bugLabel}
                title={_bugLabel}
                className="flex items-center justify-center p-1.5 sm:p-0 rounded-md sm:rounded-sm text-gray-900 hover:text-black active:text-black hover:bg-gray-100 sm:hover:bg-transparent sm:hover:underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 transition-colors whitespace-nowrap min-h-[36px] min-w-[36px] sm:min-h-0 sm:min-w-0 flex-shrink-0"
                data-testid="header-bug-fix-prompts-link"
              >
                <FriendlyBugIcon className="w-5 h-5 sm:hidden" strokeWidth={1.75} />
                <span className="hidden sm:inline text-sm md:text-base font-medium">{_bugLabel}</span>
              </button>
            </>
          );
        })()}

        {_showDailyBadge && (
          <div
            className="flex items-center flex-shrink-0"
            title={`${dailyRemaining} of ${dailyLimit} free generations remaining in the next 24 hours`}
            data-testid="daily-cap-badge"
          >
            <span className="inline-flex items-center px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-red-300 bg-white text-black text-[10px] sm:text-xs font-medium whitespace-nowrap">
              <span className="font-bold">{dailyRemaining}</span>
              <span>&nbsp;/&nbsp;{dailyLimit}</span>
              <span className="hidden md:inline">&nbsp;free generations left today</span>
              <span className="md:hidden">&nbsp;left</span>
            </span>
          </div>
        )}

        {_showCredits && (
          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
            <button
              onClick={() => navigate('/credits')}
              className="balance-button px-1 sm:px-3 py-0.5 sm:py-1.5 bg-white border-2 border-black text-black font-bold rounded-lg text-[8px] sm:text-sm cursor-pointer transition-all duration-200 hover:bg-white hover:text-red-600 hover:border-red-600 outline-none whitespace-nowrap min-w-[35px] sm:min-w-0 max-w-[60px] sm:max-w-none flex-shrink-0"
              aria-label="View your credit balance"
              data-testid="credit-balance-btn"
            >
              <span className="balance-value">{getCreditsDisplay()}</span>
              <span className="hidden sm:inline"> Credits</span>
            </button>
            <CreditUsageInfo />
          </div>
        )}

          {_showSignIn && (
            <button
              onClick={login}
              className="google-login-button login-button flex items-center space-x-1.5 md:space-x-2 px-3 md:px-5 py-1.5 md:py-2 bg-white border border-gray-300 rounded-lg text-gray-900 hover:bg-gray-50 transition-colors text-xs md:text-sm"
              aria-label="Sign in with Google"
              data-testid="google-signin-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" style={{ pointerEvents: 'none' }}>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span className="font-medium hidden sm:inline">Sign in with Google</span>
              <span className="font-medium sm:hidden">Sign in</span>
            </button>
          )}

          {_showUserMenu && <UserMenu />}
      </div>
    </header>
  );
};

export default Header;
